import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { twMerge } from 'tailwind-merge'

export interface PageLoadingProps {
  /** Additional CSS classes to apply to the container */
  className?: string
  /** Test ID for testing purposes */
  testId?: string
  /** Number of skeleton items to render (default: 5) */
  skeletonCount?: number
  /** Height of each skeleton item in pixels (default: 50) */
  skeletonHeight?: number
  /** Height of header skeleton items in pixels (default: 100) */
  headerSkeletonHeight?: number
  /** Number of header skeleton items to render (default: 1) */
  headerSkeletonCount?: number
  /** Base color for skeleton animation (default: '#eeefee') */
  baseColor?: string
  /** Highlight color for skeleton animation (default: 'rgba(255, 255, 255, 0.5)') */
  highlightColor?: string
}

/**
 * Reusable page loading component that displays skeleton placeholders
 *
 * @example
 * ```tsx
 * // Basic usage
 * {isLoading && <PageLoading />}
 *
 * // Custom configuration
 * <PageLoading
 *   skeletonCount={3}
 *   skeletonHeight={60}
 *   headerSkeletonCount={2}
 *   headerSkeletonHeight={80}
 *   testId="custom-loading"
 * />
 *
 * // With custom styling
 * <PageLoading
 *   className="p-4 bg-gray-50"
 *   baseColor="#f3f4f6"
 *   highlightColor="rgba(255, 255, 255, 0.8)"
 * />
 * ```
 */
export const EnrollmentPageLoading: FC<PageLoadingProps> = ({
  className,
  testId,
  skeletonCount = 5,
  skeletonHeight = 50,
  headerSkeletonHeight = 100,
  headerSkeletonCount = 1,
  baseColor = '#eeefee',
  highlightColor = 'rgba(255, 255, 255, 0.5)',
}) => {
  return (
    <div className={twMerge('w-full', className)} {...(testId ? { 'data-testid': testId } : {})}>
      <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>
        <Skeleton count={headerSkeletonCount} height={headerSkeletonHeight} className="mb-10" />
        <Skeleton count={skeletonCount} height={skeletonHeight} />
      </SkeletonTheme>
    </div>
  )
}
